/**
 * Geometría de paginación del PDF de smart report, aislada del DOM y de jsPDF.
 *
 * Todas las medidas entran y salen en px CSS del contenido renderizado a 210mm de ancho;
 * la conversión a milímetros se deriva de `elementWidthPx`. Al no depender de canvas ni de
 * Angular, esta función se puede ejercitar con escenarios sintéticos sin navegador.
 */

export interface PageLayoutInput {
  /** Alto total del contenido en px CSS. */
  contentHeightPx: number;
  /** Tops de los `data-pdf-block` en px CSS, relativos al contenedor. */
  breakPointsPx: number[];
  /** Ancho renderizado en px CSS (≈794 para 210mm). */
  elementWidthPx: number;
  headerHeightMm: number;
  footerHeightMm: number;
  /** El pie solo se estampa en la última franja del último grupo. */
  isLastGroup: boolean;
}

export interface PageSlice {
  offsetPx: number;
  heightPx: number;
  /** true cuando el corte no cayó en un `data-pdf-block` y hubo que partir por la mitad. */
  hardCut: boolean;
  withFooter: boolean;
}

export const PAGE_WIDTH_MM = 210;
export const PAGE_HEIGHT_MM = 297;

/**
 * Aire al pie de cada hoja: el contenido nunca se corta más abajo de esto, así el texto no queda
 * pegado al canto del papel ni a la banda del pie. Es del orden del padding lateral del reporte
 * (50px ≈ 13mm), un poco más generoso como es habitual en impresión.
 */
const BOTTOM_MARGIN_MM = 15;

/** Residuo por debajo del cual no vale la pena abrir página: se estira sobre la franja anterior. */
const TOLERANCE_MM = 1;
/**
 * Cola mínima de la última página de un grupo. Si lo que sobra no llega a esto, el corte anterior
 * se sube para repartir: es preferible dejar aire al pie de la hoja previa que cerrar el documento
 * con una hoja de dos renglones. Es del orden de un quinto de la hoja, así la última página se
 * lee como una página de contenido y no como un sobrante.
 */
const MIN_TAIL_MM = 60;
/** Excedente que el pie puede solapar antes de que justifique partir la última página. */
const FOOTER_OVERLAP_TOLERANCE_MM = 2;
/**
 * Fracción mínima de la hoja que tiene que aprovechar un corte. Sirve para dos cosas: ningún
 * corte se mueve por debajo de eso, y un borde que aprovecha menos se descarta como corte.
 */
const MIN_CUT_RATIO = 0.6;
/** Respaldo ante un input inconsistente. */
const MAX_PAGES = 200;

interface Cut {
  heightPx: number;
  hardCut: boolean;
}

export function planPages(input: PageLayoutInput): PageSlice[] {
  const { contentHeightPx, elementWidthPx, headerHeightMm, footerHeightMm, isLastGroup } = input;
  if (!(contentHeightPx > 0) || !(elementWidthPx > 0)) return [];

  const cssPxPerMm = elementWidthPx / PAGE_WIDTH_MM;
  const tolerancePx = TOLERANCE_MM * cssPxPerMm;
  const minTailPx = MIN_TAIL_MM * cssPxPerMm;
  const overlapPx = FOOTER_OVERLAP_TOLERANCE_MM * cssPxPerMm;

  const usableHeightMm = PAGE_HEIGHT_MM - BOTTOM_MARGIN_MM;
  const headerOnlyBudgetPx = Math.max(0, (usableHeightMm - headerHeightMm) * cssPxPerMm);
  const withFooterBudgetPx = Math.max(0, (usableHeightMm - headerHeightMm - footerHeightMm) * cssPxPerMm);
  if (headerOnlyBudgetPx <= tolerancePx) return [];

  const breakPointsPx = normalizeBreaks(input.breakPointsPx, contentHeightPx);
  const slices: PageSlice[] = [];
  let offsetPx = 0;

  while (contentHeightPx - offsetPx > tolerancePx && slices.length < MAX_PAGES) {
    let cut = chooseCut(offsetPx, headerOnlyBudgetPx, breakPointsPx, contentHeightPx, minTailPx);

    // La última franja del último grupo tiene que convivir con el pie. Si se pasa apenas del
    // presupuesto, el pie solapa esos milímetros: partir ahí dejaría una hoja con dos píxeles
    // de contenido, que es justo el bug de la página en blanco.
    const reachesEnd = offsetPx + cut.heightPx >= contentHeightPx - tolerancePx;
    const overshootPx = cut.heightPx - withFooterBudgetPx;
    if (isLastGroup && reachesEnd && withFooterBudgetPx > 0 && overshootPx > overlapPx) {
      cut = chooseCut(offsetPx, withFooterBudgetPx, breakPointsPx, contentHeightPx, minTailPx);
    }

    if (cut.heightPx <= tolerancePx) break;

    slices.push({ offsetPx, heightPx: cut.heightPx, hardCut: cut.hardCut, withFooter: false });
    offsetPx += cut.heightPx;
  }

  if (!slices.length) return slices;

  // El residuo de redondeo se estira sobre la última franja: no se pierde contenido y no se
  // abre una hoja por menos de un milímetro.
  const last = slices[slices.length - 1];
  const residualPx = contentHeightPx - offsetPx;
  if (residualPx > 0 && residualPx <= tolerancePx) last.heightPx += residualPx;
  if (isLastGroup) last.withFooter = true;

  return slices;
}

/**
 * Elige el alto de la franja que arranca en `offsetPx`: prefiere el borde más lejano que quepa en
 * el presupuesto y, si no hay ninguno aprovechable, parte duro evitando dejar una cola huérfana.
 */
function chooseCut(
  offsetPx: number,
  budgetPx: number,
  breakPointsPx: number[],
  contentHeightPx: number,
  minTailPx: number,
): Cut {
  const remainingPx = contentHeightPx - offsetPx;
  if (remainingPx <= budgetPx) return { heightPx: remainingPx, hardCut: false };

  const candidates = breakPointsPx.filter((bp) => bp > offsetPx).map((bp) => bp - offsetPx);
  const aligned = lastAtMost(candidates, budgetPx);

  // Un borde que deja la hoja a menos de MIN_CUT_RATIO no sirve como corte: pasa cuando el bloque
  // siguiente es más alto que una página entera, así que ningún borde suyo entra en el presupuesto
  // y el "mejor" candidato es el que abre ese bloque. Cortar ahí es el bug de la primera hoja con
  // solo el aviso: el resumen ejecutivo mide página y media, así que el único borde que cabía era
  // su propio arranque. Con este descarte se llena la página partiendo dentro del bloque.
  if (aligned !== null && aligned >= budgetPx * MIN_CUT_RATIO) {
    const tailPx = remainingPx - aligned;
    if (tailPx >= minTailPx) return { heightPx: aligned, hardCut: false };

    // La cola quedaría como hoja casi vacía: retroceder al borde más lejano que le deje `minTailPx`.
    const pulledPx = lastAtMost(candidates, remainingPx - minTailPx);
    if (pulledPx !== null && pulledPx >= budgetPx * MIN_CUT_RATIO) return { heightPx: pulledPx, hardCut: false };
    return { heightPx: aligned, hardCut: false };
  }

  let heightPx = budgetPx;
  const tailPx = remainingPx - heightPx;
  if (tailPx > 0 && tailPx < minTailPx) {
    const pulledPx = heightPx - (minTailPx - tailPx);
    if (pulledPx >= budgetPx * MIN_CUT_RATIO) heightPx = pulledPx;
  }
  return { heightPx, hardCut: true };
}

/**
 * Ordena, descarta los bordes fuera de rango y colapsa los repetidos.
 *
 * El umbral es diminuto a propósito: los bordes finos vienen de filas de píxeles del canvas, que
 * están separadas por 1/escala (0.5px al renderizar a escala 2). Un umbral mayor los descartaría
 * casi todos y la paginación se quedaría solo con los bordes de bloque.
 */
function normalizeBreaks(breakPointsPx: number[], contentHeightPx: number): number[] {
  const sorted = (breakPointsPx || [])
    .filter((bp) => Number.isFinite(bp) && bp > 0 && bp < contentHeightPx)
    .sort((a, b) => a - b);

  const unique: number[] = [];
  for (const bp of sorted) {
    if (!unique.length || bp - unique[unique.length - 1] > 0.01) unique.push(bp);
  }

  return unique;
}

/** Mayor valor de una lista ascendente que no supera `limit`. */
function lastAtMost(values: number[], limit: number): number | null {
  let best: number | null = null;
  for (const value of values) {
    if (value > limit) break;
    best = value;
  }
  return best;
}
